import React from 'react';
import { MessageCircle, Clock, Plus, Send } from 'lucide-react';
import { formatDate } from './utils';

export const ThreadCard = ({ thread, onClick }) => {
  return (
    <button
      onClick={() => onClick(thread.id)}
      className="w-full p-5 rounded-xl mb-4 text-left transition-all hover:scale-[1.01]"
      style={{ background: '#FAF7F3', border: '1px solid #F7F1EE' }}
      data-testid={`thread-${thread.id}`}
    >
      <h3 
        className="font-medium mb-2"
        style={{ color: '#1A1A2E', fontFamily: 'Jost, sans-serif' }}
      >
        {thread.title}
      </h3>
      <p 
        className="text-sm mb-3 line-clamp-2"
        style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}
      >
        {thread.content}
      </p>
      <div className="flex items-center gap-4 text-xs" style={{ color: '#8A8798' }}>
        <span className="flex items-center gap-1" style={{ fontFamily: 'Jost, sans-serif' }}>
          <MessageCircle className="w-3.5 h-3.5" />
          {thread.reply_count || 0} replies
        </span>
        <span className="flex items-center gap-1" style={{ fontFamily: 'Jost, sans-serif' }}>
          <Clock className="w-3.5 h-3.5" />
          {formatDate(thread.last_activity || thread.created_at)}
        </span>
        <span style={{ fontFamily: 'Jost, sans-serif' }}>
          by {thread.author_name}
        </span>
      </div>
    </button>
  );
};

export const ThreadList = ({ threads, onThreadClick }) => {
  if (threads.length === 0) {
    return (
      <div 
        className="text-center py-12"
        style={{ color: '#8A8798', fontFamily: 'Jost, sans-serif' }}
      >
        <p>No discussions yet. Start a new topic.</p>
      </div>
    );
  }
  
  return (
    <div data-testid="threads-list">
      {threads.map((thread) => (
        <ThreadCard 
          key={thread.id} 
          thread={thread} 
          onClick={onThreadClick}
        />
      ))}
    </div>
  );
};

export const NewThreadForm = ({ 
  show, 
  setShow, 
  title,
  setTitle,
  content, 
  setContent, 
  isAnonymous, 
  setIsAnonymous, 
  onSubmit, 
  submitting 
}) => {
  if (!show) {
    return (
      <button
        onClick={() => setShow(true)}
        className="w-full p-4 rounded-xl mb-6 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
        style={{ 
          background: '#FAF7F3', 
          border: '1px dashed #EAE4DC',
          color: '#6B6878',
          fontFamily: 'Jost, sans-serif'
        }}
        data-testid="new-thread-button"
      >
        <Plus className="w-4 h-4" />
        Start a discussion
      </button>
    );
  }
  
  return (
    <div 
      className="p-5 rounded-xl mb-6"
      style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}
      data-testid="new-thread-form"
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Discussion title..."
        className="w-full p-3 rounded-lg mb-3 focus:outline-none"
        style={{ 
          background: 'rgba(26,26,46,0.06)', 
          color: '#1A1A2E',
          border: '1px solid #F7F1EE',
          fontFamily: 'Jost, sans-serif'
        }}
        data-testid="new-thread-title"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="What would you like to discuss?"
        className="w-full p-4 rounded-lg resize-none focus:outline-none"
        style={{ 
          background: 'rgba(26,26,46,0.06)', 
          color: '#1A1A2E',
          border: '1px solid #F7F1EE',
          fontFamily: 'Jost, sans-serif',
          minHeight: '120px'
        }}
        data-testid="new-thread-content"
      />
      
      <div className="flex items-center justify-between mt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="rounded"
            data-testid="new-thread-anonymous"
          />
          <span 
            className="text-sm"
            style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}
          >
            Post anonymously
          </span>
        </label>
        
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShow(false);
              setTitle('');
              setContent('');
            }}
            className="px-4 py-2 rounded-full text-sm transition-opacity hover:opacity-70"
            style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || title.length < 5 || content.length < 20}
            className="px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-all hover:scale-105 disabled:opacity-50"
            style={{ 
              background: '#C9A84C', 
              color: '#FBF9F6',
              fontFamily: 'Jost, sans-serif'
            }}
            data-testid="submit-thread"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Creating...' : 'Create Discussion'}
          </button>
        </div>
      </div>
    </div>
  );
};
